import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Button, Group, Loader, Text, Title } from '@mantine/core';
import { ClipboardList, X } from 'lucide-react';
import { todoApi } from '../../api/todoApi';
import { TodoItem } from '../../types/todo';
import { useUser } from '../../contexts/UserContext';

interface TaskPreviewDialogProps {
  isOpen: boolean;
  taskId: number | null;
  onClose: () => void;
  onStatusChange?: (taskId: number, completed: boolean) => void;
}

export const TaskPreviewDialog: React.FC<TaskPreviewDialogProps> = ({ isOpen, taskId, onClose, onStatusChange }) => {
  const [task, setTask] = useState<TodoItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { currentUser } = useUser();

  useEffect(() => {
    const load = async () => {
      if (!isOpen || !taskId || !currentUser?.email) return;
      setIsLoading(true);
      try {
        const t = await todoApi.getTodo(taskId, currentUser.email);
        setTask(t);
      } catch (e) {
        console.error('Failed to load task', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [isOpen, taskId, currentUser?.email]);

  const toggleComplete = async () => {
    if (!task || !currentUser?.email) return;
    try {
      const updated = await todoApi.toggleTodo(task.id, currentUser.email);
      setTask(updated);
      onStatusChange?.(updated.id, !!updated.isCompleted);
    } catch (e) {
      console.error('Failed to toggle task', e);
    }
  };

  if (!isOpen) return null;

  const modal = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10200,
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Box
        style={{
          backgroundColor: 'var(--app-surface)',
          border: '1px solid var(--app-border)',
          borderRadius: 12,
          padding: 24,
          width: 'min(520px, 100%)',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Group justify="space-between" align="center" mb="md">
          <Group gap="sm">
            <ClipboardList size={20} />
            <Title order={3}>Task</Title>
          </Group>
          <Button variant="subtle" size="sm" onClick={onClose} style={{ padding: 4, minHeight: 'auto', width: 28, height: 28 }}>
            <X size={16} />
          </Button>
        </Group>
        {isLoading ? (
          <Group justify="center">
            <Loader />
          </Group>
        ) : task ? (
          <>
            <Text fw={600} mb="xs">{task.title}</Text>
            {task.description && (
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{task.description}</Text>
            )}
            <Group justify="space-between" mt="md">
              <Text size="sm" c={task.isCompleted ? 'teal' : 'dimmed'}>
                {task.isCompleted ? 'Completed' : 'Not completed'}
              </Text>
              <Button size="xs" variant="light" onClick={toggleComplete}>
                {task.isCompleted ? 'Mark as Incomplete' : 'Mark as Complete'}
              </Button>
            </Group>
          </>
        ) : (
          <Text size="sm">Task not found.</Text>
        )}
      </Box>
    </div>
  );

  return createPortal(modal, document.body);
};

export default TaskPreviewDialog;
